// React
import * as React from "react";
import _ from 'lodash';

// Internal components
import * as WidgetsThingsStructs from "./../WidgetsThingsStructs";
import { BoardTheming } from '@UI/Dashboard/BoardTheming';
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import { isDefined, IsNullOrEmpty, IsNullOrWhiteSpace } from '@Utils/Micro';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing, WidgetType } from '@UI/Widgets/WidgetStructs';
import { IWidgetDataValidateResp } from "@UI/Widgets/IWidgetDataValidateResp";
import { Canvas } from '@UI/Canvas/Canvas';
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { SettingsList } from '@Primitive/SettingsList';
import { WebThing, WebThingPort } from '@API/WebThing';
import { WebThingPortType } from '@API/WebNodeTypes';
import { Plegma } from '@API/Api';
import { WebNode } from '@API/WebNode';
import { DashboardDescriptor } from '@UI/Dashboard/DashboardStructs';
import { BoardThings } from '@UI/Dashboard/BoardThings';
import { DashboardManager } from '@UI/Dashboard/DashboardManager';

// =================================================================================================
export interface PagePortalData {
    WidthListThreshold: number;
}
// -------------------------------------------------------------------------------------------------
export interface PagePortalState {
    loadedPageId: string;
    page: DashboardDescriptor;
}
// -------------------------------------------------------------------------------------------------
const enum PortKeys {
    PageId = "PageId"
}
// -------------------------------------------------------------------------------------------------
export class PagePortal extends React.Component<WidgetsThingsStructs.PartialIWidgetThingsProps<PagePortalData>, PagePortalState> {
    // ---------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<PagePortalData>) {
        super(props);
        this.state = {
            loadedPageId: undefined,
            page: undefined
        }

        let newState = this.loadFromProps(this.props);
        if (isDefined(newState))
            this.state = _.defaults(this.state, newState);
    }
    // ---------------------------------------------------------------------------------------------
    componentWillUnmount() {
        // stop polling
        let page = this.state.page;
        if (isDefined(page)) {
            BoardThings.GetInstance(this.props.NodeKey)?.PulseThingStop(page);
        }
    }
    // ---------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: WidgetsThingsStructs.PartialIWidgetThingsProps<PagePortalData>) {
        if (prevProps.PortVersion != this.props.PortVersion) {
            let newState = this.loadFromProps(this.props);
            if (isDefined(newState))
                this.setState(newState);
        }
    }
    // ---------------------------------------------------------------------------------------------
    loadFromProps(nextProps: WidgetsThingsStructs.PartialIWidgetThingsProps<PagePortalData>) {
        if (_.has(nextProps.PortsStates, PortKeys.PageId)) {
            let port = nextProps.PortsStates[PortKeys.PageId];
            if (port?.State != this.state.loadedPageId) {
                // If we have loaded a different page id start loading process
                let board = BoardThings.GetInstance(this.props.NodeKey);
                if (isDefined(board)) {

                    // stop polling for previous page
                    let prevPage = this.state.page;
                    if (isDefined(prevPage)) {
                        board.PulseThingStop(prevPage);
                    }

                    if (!IsNullOrWhiteSpace(port?.State)) {
                        let newId = port.State;
                        let page = board.getPageById(newId);

                        // check if the specific page id is the page that the widget exist
                        let isNotExist = _.find(page?.Widgets, w =>
                            (w.WidgetType == WidgetType.Thing) &&
                            ((w as any).ThingKey == this.props.ThingKey)) == null;

                        if (isDefined(page) && isNotExist) {
                            // active set and start polling
                            board.UpdateActiveDashboardPortState(page.WebModuleKey);
                            board.PulseThingStart(page);

                            return { page, loadedPageId: newId };
                        } else
                            return { page: null, loadedPageId: null };
                    }
                    else {
                        return { page: null, loadedPageId: null };
                    }
                }
            }
        }
        return null;
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        const { loadedPageId, page } = this.state;
        if (!isDefined(loadedPageId) ||
            !isDefined(page))
            return null;

        const { width, height } = this.props;
        let inMobileMode = width < this.props.WidgetData.WidthListThreshold;

        const boardThing = BoardThings.GetInstance(this.props.NodeKey);
        const Layout = DashboardManager.getLayout(
            page,
            false,
            inMobileMode,
            height,
            isDefined(boardThing) ? boardThing.getWidgetsVisibility() : {});

        // check if layout was found
        if (!isDefined(Layout))
            return null;

        let settings = BoardTheming.getDashboardConfiguration(this.props.NodeKey, page.Settings);
        return <Canvas
            key={this.props.key + "-canvas"}
            width={width}
            height={height}
            WidthListThreshold={this.props.WidgetData.WidthListThreshold}
            SizeW={settings.NumberOfVerticalGridSpaces}
            Layout={Layout.Layout}
            CompactLayout={settings.VerticalCompact}
            InEditMode={false}
            Margin={settings.Margin}
            BackgroundImage={settings.BackgroundImage}
            GridLayout={settings.GridLayout}
            CanvasBackgroundColor={settings.CanvasBackgroundColor}
            Padding={settings.Padding}
            ElementSettings={{
                zDepth: settings.ZDepth,
                BackgroundColor: settings.WidgetBackgroundColor,
                NoBackground: settings.WidgetNoBackground,
                BorderRadius: settings.BorderRadius,
                Outline: settings.Outline
            }}
            HeightPixels={Layout.heightPixels}
            rootStyle={{
                width: 'initial',
                height: 'initial',
                overflow: 'initial'
            }}
        />
    }
}
// =================================================================================================
class PagePortalSettings extends React.PureComponent<IWidgetSettingsProps<PagePortalData>> {
    constructor(props: IWidgetSettingsProps<PagePortalData>) {
        super(props);
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<PagePortalData>[] = [
            {
                name: "",
                elements: [
                    {
                        type: SettingsType.Slider,
                        dataPath: "WidthListThreshold",
                        label: "Width Mobile Threshold",
                        minValue: 0,
                        maxValue: 2000,
                        stepValue: 10,
                        valuePosfix: "px",
                        tooltip: "Width screen threshold in pixels. If the size less than the threshold, mobile view is enabled."
                    }
                ]
            }
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isDefined(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
}
// =================================================================================================

export class PagePortalWidget extends IWidgetThingsClass<PagePortalData> {
    Render(key: string, width: number, height: number): React.ReactNode {
        return <PagePortal />;
    }
    // ---------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <PagePortalSettings
            key={'settings-' + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ---------------------------------------------------------------------------------------------
    private createThingPorts(widgetData: PagePortalData): WebThingPort[] {
        let ports: WebThingPort[] = [];

        // TODO: Set the Ports
        ports.push(new WebThingPort(
            {
                Name: 'PageId',
                PortKey: PortKeys.PageId,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: 'Page id for showing',
                State: ''
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues('PortKey', data))
        );
        return ports;
    }
    // ---------------------------------------------------------------------------------------------
    updateThing() {
        let widgetData = this.WidgetData,
            webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        webThing.Ports = {};

        // Add thing's ports.
        this
            .createThingPorts(widgetData)
            .forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode
            .GetModule(this.WebModuleKey)
            .UpdateThing(webThing);
    }
    // ---------------------------------------------------------------------------------------------
    initThing() {
        let widgetData = (this.WidgetData as PagePortalData);

        // TOOD: Update thing description and widget icon

        // Create plegma thing.
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create thing's ports and web thing.
        let ports = this.createThingPorts(widgetData);
        let webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ---------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): PagePortalData {
        return {
            WidthListThreshold: 800
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return PagePortalWidget.GetDefaultWidgetDataStatic(); }
    // ---------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        // TODO: Update the widget Description
        return {
            Title: 'Page Portal',
            WidgetTitle: '',
            Key: 'PagePortalWidget',
            Type: 'PagePortalWidget',
            Data: PagePortalWidget.GetDefaultWidgetDataStatic(),
            Description: 'Page portal',
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + '/svg/tile.svg'
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        const res = {};

        // expose title if defined
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        return res;
    }
    // ---------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        // todo: set schema validators
        return { valid: true, errors: "" };
    }
    // ---------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: PagePortalData): PagePortalData {
        return data;
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultValues() { return {} }
    // ---------------------------------------------------------------------------------------------
}